import { LngLat } from '@yandex/ymaps3-types';
import type { YMap } from '@yandex/ymaps3-types/imperative/YMap';
import { YMapLocationRequest } from '@yandex/ymaps3-types/imperative/YMap';
import {
  Feature as ClustererFeature,
  IClusterMethod
} from '@yandex/ymaps3-types/packages/clusterer';
import { useCallback, useRef } from 'react';

import { Shop, ShopList } from '@/api/backend/contract/Shop';
import { ReactifyApi, Ymaps3Clusterer } from '@/api/yandex-maps';
import { UiKitMapZoomControls } from '@/ui-kit/map-zoom-controls';
import { UiKitPickupCluster } from '@/ui-kit/pickup-cluster';
import { UiKitPickupMarker } from '@/ui-kit/pickup-marker';
import { MARGIN, MOBILE_MARGIN } from '@/util/map-point-maps/consts';
import { PointFeature } from '@/util/map-point-maps/types';

import { styles } from './UiKitMapContent.styles';

export interface MapBoundsChangePayload {
  latitudeL: number;
  latitudeR: number;
  longitudeL: number;
  longitudeR: number;
  zoom: number;
}

export interface UiKitMapContentProps {
  reactifyApi: ReactifyApi;
  clusterer: Ymaps3Clusterer;
  gridSizedMethod: IClusterMethod;
  pointsList: PointFeature[] | ShopList;
  selectedPoint: PointFeature | Shop | null;
  location: YMapLocationRequest;
  isDesktop: boolean;
  type?: 'default' | 'shops';
  onMarkerClick: (feature: PointFeature | Shop) => void;
  onClusterClick: (features: ClustererFeature[]) => void;
  onBoundsChange?: (bounds: MapBoundsChangePayload) => void;
  onZoomIn?: (center: LngLat, zoom: number) => void;
  onZoomOut?: (center: LngLat, zoom: number) => void;
}

/**
 * Компонент для отображения карты с маркерами и кластерами
 */
export function UiKitMapContent({
  reactifyApi,
  clusterer,
  gridSizedMethod,
  pointsList,
  selectedPoint,
  location,
  isDesktop,
  type = 'default',
  onMarkerClick,
  onClusterClick,
  onBoundsChange,
  onZoomIn,
  onZoomOut
}: UiKitMapContentProps) {
  const slots = styles();
  const mapRef = useRef<YMap | null>(null);

  const {
    YMap,
    YMapDefaultSchemeLayer,
    YMapDefaultFeaturesLayer,
    YMapMarker,
    YMapListener
  } = reactifyApi;

  const { YMapClusterer } = clusterer;

  const handleZoomInClick = useCallback(() => {
    if (!onZoomIn || !mapRef.current) return;

    const map = mapRef.current;
    onZoomIn([map.center[0], map.center[1]], map.zoom);
  }, [onZoomIn]);

  const handleZoomOutClick = useCallback(() => {
    if (!onZoomOut || !mapRef.current) return;

    const map = mapRef.current;
    onZoomOut([map.center[0], map.center[1]], map.zoom);
  }, [onZoomOut]);

  const handleMapUpdate = useCallback(
    (update: { location: { zoom: number } }) => {
      if (!onBoundsChange || !mapRef.current) return;
      const b = mapRef.current.bounds;
      if (!b?.length) return;
      const [[lng0, lat0], [lng1, lat1]] = b as [[number, number], [number, number]];
      onBoundsChange({
        latitudeL: Math.min(lat0, lat1),
        latitudeR: Math.max(lat0, lat1),
        longitudeL: Math.min(lng0, lng1),
        longitudeR: Math.max(lng0, lng1),
        zoom: update.location.zoom
      });
    },
    [onBoundsChange]
  );

  /**
   * Рендер маркера
   */
  const marker = useCallback(
    (feature: ClustererFeature) => (
      <YMapMarker
        coordinates={[feature.geometry.coordinates[0], feature.geometry.coordinates[1]]}
      >
        <UiKitPickupMarker
          feature={feature as PointFeature}
          onClick={onMarkerClick}
          type={type}
          isSelected={selectedPoint?.id === feature.id}
        />
      </YMapMarker>
    ),
    [YMapMarker, onMarkerClick, selectedPoint?.id, type]
  );

  /**
   * Рендер кластера
   */
  const cluster = useCallback(
    (coordinates: LngLat, features: ClustererFeature[]) => (
      <YMapMarker coordinates={coordinates}>
        <UiKitPickupCluster
          features={features}
          onClick={onClusterClick}
        />
      </YMapMarker>
    ),
    [YMapMarker, onClusterClick]
  );

  return (
    <div className={slots.base()}>
      <YMap
        ref={mapRef}
        location={location}
        margin={isDesktop ? MARGIN : MOBILE_MARGIN}
      >
        {onBoundsChange && YMapListener ? (
          <YMapListener onUpdate={handleMapUpdate} />
        ) : null}
        <YMapDefaultSchemeLayer />
        <YMapDefaultFeaturesLayer />

        <YMapClusterer
          features={pointsList}
          method={gridSizedMethod}
          marker={marker}
          cluster={cluster}
        />
      </YMap>
      {onZoomIn && onZoomOut ? (
        <UiKitMapZoomControls
          onZoomIn={handleZoomInClick}
          onZoomOut={handleZoomOutClick}
        />
      ) : null}
    </div>
  );
}
